import React, { useState, useEffect, useCallback } from "react";
import { MessageSquareHeart, Trash2, X, Clock, Trophy, Flag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";

const AdminCommunity = () => {
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [reportsRes, statsRes] = await Promise.all([
        api.get(`/admin/community/reports?status=${filter}`),
        api.get("/admin/community/stats"),
      ]);
      setReports(reportsRes.data.reports);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Failed to load community data");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDismiss = async (reportId) => {
    try {
      await api.post(`/admin/community/reports/${reportId}/dismiss`);
      toast.success("Report dismissed");
      fetchData();
    } catch (error) {
      toast.error("Failed to dismiss report");
    }
  };

  const handleRemovePost = async (postId) => {
    try {
      await api.delete(`/admin/community/posts/${postId}`);
      toast.success("Post removed from community");
      fetchData();
    } catch (error) {
      toast.error("Failed to remove post");
    }
  };

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

  return (
    <AdminLayout>
      <div className="bg-gray-100 min-h-screen">
        <div className="bg-white shadow-sm px-4 py-4 sticky top-[52px] z-10">
          <div className="flex items-center gap-3 mb-4">
            <MessageSquareHeart className="w-6 h-6 text-[#1C3A7A]" />
            <h1 className="text-xl font-bold text-gray-900">Community Moderation</h1>
          </div>

          {stats && (
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-[#1C3A7A]" data-testid="stat-total-posts">{stats.total_posts}</p>
                <p className="text-xs text-gray-500">Active Posts</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-[#FAB710]" data-testid="stat-milestones">{stats.milestone_posts}</p>
                <p className="text-xs text-gray-500">Milestones</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-red-500" data-testid="stat-pending-reports">{stats.pending_reports}</p>
                <p className="text-xs text-gray-500">Pending Reports</p>
              </div>
            </div>
          )}

          <div className="flex gap-2 overflow-x-auto pb-1">
            {["pending", "dismissed", "actioned"].map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  filter === status ? "bg-[#1C3A7A] text-white" : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                }`}
                data-testid={`filter-${status}`}
              >
                {status}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#1C3A7A] border-t-transparent"></div>
          </div>
        ) : reports.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <Flag className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No {filter} reports</h3>
            <p className="text-gray-600">The community is looking healthy!</p>
          </div>
        ) : (
          <div className="p-4 space-y-4 max-w-3xl mx-auto">
            {reports.map((report) => (
              <div key={report.id} className="bg-white rounded-xl p-4 shadow-sm" data-testid={`report-card-${report.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {report.post_type === "milestone" ? (
                      <Trophy className="w-5 h-5 text-[#FAB710]" />
                    ) : (
                      <Flag className="w-5 h-5 text-orange-500" />
                    )}
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                      {report.status}
                    </span>
                    {report.post_removed && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">post removed</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <Clock className="w-3 h-3" />
                    {formatDate(report.created_at)}
                  </div>
                </div>

                <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Reported Post</p>
                  <p className="text-sm text-gray-900">{report.post_content || "(content unavailable)"}</p>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Author</p>
                    <p className="font-medium text-gray-900 text-sm truncate">{report.reported_user?.name || "Unknown"}</p>
                    <p className="text-xs text-gray-500 truncate">{report.reported_user?.email}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Reported by • Reason</p>
                    <p className="font-medium text-gray-900 text-sm truncate">{report.reporter?.name || "Unknown"}</p>
                    <p className="text-xs text-gray-600 truncate">{report.reason}</p>
                  </div>
                </div>

                {report.status === "pending" && (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDismiss(report.id)}
                      className="flex-1"
                      data-testid={`dismiss-btn-${report.id}`}
                    >
                      <X className="w-4 h-4 mr-1" /> Dismiss
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleRemovePost(report.post_id)}
                      className="flex-1 bg-red-500 hover:bg-red-600"
                      data-testid={`remove-post-btn-${report.id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Remove Post
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminCommunity;
